import { useCallback, useEffect, useState } from "react";
import type { Lead } from "@/types/lead";
import type { Opportunity } from "@/types/opportunity";
import type { OppTodoItem } from "@/lib/opportunity/oppTodoItemTypes";
import { isOppTodoItemOpen } from "@/lib/opportunity/oppTodoItemTypes";
import {
  classifyOppTodoChecklistUrgency,
  sortOppTodoChecklistRows,
  type OppTodoChecklistUrgency,
} from "@/lib/opportunity/oppTodoChecklistUrgency";
import { salesChecklistLocalCalendarDay } from "@/lib/salesDailyChecklistClient";
import { getLinkedOpportunityIdForLead, getOpportunity } from "@/lib/opportunity/opportunityClient";
import { listOppTodoItems } from "@/lib/opportunity/oppTodoItemClient";

export type SalesConsoleDealTodoRow = {
  item: OppTodoItem;
  urgency: OppTodoChecklistUrgency;
};

const DEAL_TODO_STATUSES = new Set(["ordered", "in_process"]);

export function useSalesConsoleDealTodos(selectedLead: Lead | null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [rows, setRows] = useState<SalesConsoleDealTodoRow[]>([]);

  const load = useCallback(async () => {
    if (!selectedLead) {
      setOpportunity(null);
      setRows([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    let opportunityId = selectedLead.linked_opportunity_id?.trim() || null;
    if (!opportunityId) {
      const linkR = await getLinkedOpportunityIdForLead(selectedLead.id);
      if (!linkR.ok) {
        setLoading(false);
        setError(linkR.error);
        setOpportunity(null);
        setRows([]);
        return;
      }
      opportunityId = linkR.data.opportunity_id;
    }

    if (!opportunityId) {
      setLoading(false);
      setOpportunity(null);
      setRows([]);
      return;
    }

    const oppR = await getOpportunity(opportunityId);
    if (!oppR.ok) {
      setLoading(false);
      setError(oppR.error);
      setOpportunity(null);
      setRows([]);
      return;
    }

    const opp = oppR.data.opportunity;
    setOpportunity(opp);

    if (!DEAL_TODO_STATUSES.has(opp.status)) {
      setLoading(false);
      setRows([]);
      return;
    }

    const todosR = await listOppTodoItems(opportunityId);
    setLoading(false);
    if (!todosR.ok) {
      setError(todosR.error);
      setRows([]);
      return;
    }

    const today = salesChecklistLocalCalendarDay();
    const open = todosR.data.items.filter(isOppTodoItemOpen);
    const mapped = open.map((item) => ({
      item,
      urgency: classifyOppTodoChecklistUrgency({ dueDate: item.due_date, todayYmd: today }),
      sort_order: item.sort_order,
      due_date: item.due_date,
    }));
    setRows(
      sortOppTodoChecklistRows(mapped).map(({ item, urgency }) => ({ item, urgency })),
    );
  }, [selectedLead]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    loading,
    error,
    opportunity,
    rows,
    reload: load,
  };
}
